import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Phone, Signal, MessageCircle, RefreshCw, Loader2, CheckCircle, AlertTriangle, XCircle, Zap, TrendingUp, X, Bell, History, ChevronRight, Settings, ThumbsUp, ThumbsDown, Archive } from 'lucide-react';

const WhatsAppHealth = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [instancesHistory, setInstancesHistory] = useState({});
  const [aptidaoConfig, setAptidaoConfig] = useState(() => {
    const saved = localStorage.getItem('jtracker_aptidao_config');
    return saved ? (() => {
      const parsed = JSON.parse(saved);
      // Migrar formato antigo pra novo (múltiplas condições)
      if (!parsed.conditions) {
        return { logic: 'AND', conditions: [{ condition: parsed.condition || 'is', quality: parsed.quality || 'GREEN', days: parsed.days || 3 }] };
      }
      return parsed;
    })() : { logic: 'AND', conditions: [{ condition: 'is', quality: 'GREEN', days: 3 }] };
  });
  const [showAptidaoConfig, setShowAptidaoConfig] = useState(false);
  const [hideDisconnected, setHideDisconnected] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/whatsapp/health?archived=' + (showArchived ? 1 : 0));
      setData(response.data);
      
      // Buscar histórico de todas as instâncias para as bolinhas
      const instances = response.data?.instances || [];
      const historyPromises = instances.map(async (inst) => {
        try {
          const histRes = await api.get(`/whatsapp/health/history/${inst.instanceId}?days=5`);
          return { instanceId: inst.instanceId, history: histRes.data.history || [] };
        } catch (e) {
          return { instanceId: inst.instanceId, history: [] };
        }
      });
      
      const histories = await Promise.all(historyPromises);
      const histMap = {};
      histories.forEach(h => { histMap[h.instanceId] = h.history; });
      setInstancesHistory(histMap);
    } catch (error) {
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/whatsapp/alerts?days=30');
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Erro alertas:', error);
    }
  };

  const syncData = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/whatsapp/health/sync');
      setData(response.data);
      if (response.data.alerts?.length > 0) fetchAlerts();
      // Recarregar históricos
      fetchData();
    } catch (error) {
      console.error('Erro sync:', error);
    } finally {
      setSyncing(false);
    }
  };

  const fetchHistory = async (instanceId) => {
    setLoadingHistory(true);
    try {
      const response = await api.get(`/whatsapp/health/history/${instanceId}?days=30`);
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Erro histórico:', error);
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const openModal = (instance) => {
    setSelectedInstance(instance);
    fetchHistory(instance.instanceId);
  };

  const closeModal = () => {
    setSelectedInstance(null);
    setHistory([]);
  };

  const markAllRead = async () => {
    try {
      await api.post('/whatsapp/alerts/read-all');
      setAlerts(alerts.map(a => ({ ...a, readAt: new Date() })));
    } catch (error) {
      console.error('Erro:', error);
    }
  };

  const saveAptidaoConfig = (config) => {
    setAptidaoConfig(config);
    localStorage.setItem('jtracker_aptidao_config', JSON.stringify(config));
    setShowAptidaoConfig(false);
  };

  const checkSingleCondition = (hist, cond) => {
    const lastNDays = hist.slice(0, cond.days);
    if (lastNDays.length < cond.days) return null;
    
    if (cond.condition === 'is') {
      if (cond.quality === 'GREEN') return lastNDays.every(h => h.qualityRating === 'GREEN');
      if (cond.quality === 'YELLOW') return lastNDays.every(h => h.qualityRating === 'GREEN' || h.qualityRating === 'YELLOW');
      return lastNDays.every(h => h.qualityRating === cond.quality);
    } else {
      if (cond.quality === 'RED') return lastNDays.every(h => h.qualityRating !== 'RED');
      if (cond.quality === 'YELLOW') return lastNDays.every(h => h.qualityRating !== 'YELLOW' && h.qualityRating !== 'RED');
      return lastNDays.every(h => h.qualityRating !== 'GREEN');
    }
  };

  const checkAptidao = (instanceId) => {
    const hist = instancesHistory[instanceId] || [];
    if (hist.length === 0) return null;
    
    const conditions = aptidaoConfig.conditions || [{ condition: aptidaoConfig.condition || 'is', quality: aptidaoConfig.quality || 'GREEN', days: aptidaoConfig.days || 3 }];
    const results = conditions.map(cond => checkSingleCondition(hist, cond));
    if (results.some(r => r === null)) return null;
    
    if (aptidaoConfig.logic === 'OR') return results.some(r => r === true);
    return results.every(r => r === true);
  };

  useEffect(() => { 
    fetchData(); 
    fetchAlerts();
  }, [showArchived]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin text-blue-500" size={40} />
      </div>
    );
  }

  const summary = data?.summary || { total: 0, green: 0, yellow: 0, red: 0, connected: 0 };
  const allInstances = data?.instances || [];
  const instances = hideDisconnected ? allInstances.filter(i => i.status === 'CONNECTED') : allInstances;
  const disconnectedCount = allInstances.filter(i => i.status !== 'CONNECTED').length;
  const unreadAlerts = alerts.filter(a => !a.readAt).length;

  const getQualityBadge = (quality) => {
    switch (quality) {
      case 'GREEN': return <span className="flex items-center gap-1 text-green-400 bg-green-400/20 px-3 py-1 rounded-full text-sm font-medium"><CheckCircle size={14} /> Alta</span>;
      case 'YELLOW': return <span className="flex items-center gap-1 text-yellow-400 bg-yellow-400/20 px-3 py-1 rounded-full text-sm font-medium"><AlertTriangle size={14} /> Média</span>;
      case 'RED': return <span className="flex items-center gap-1 text-red-400 bg-red-400/20 px-3 py-1 rounded-full text-sm font-medium"><XCircle size={14} /> Baixa</span>;
      default: return <span className="text-gray-400 bg-gray-400/20 px-3 py-1 rounded-full text-sm">Desconhecido</span>;
    }
  };

  const getQualityDot = (quality) => {
    switch (quality) {
      case 'GREEN': return 'bg-green-500';
      case 'YELLOW': return 'bg-yellow-500';
      case 'RED': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getQualityDotTitle = (quality) => {
    switch (quality) {
      case 'GREEN': return 'Alta';
      case 'YELLOW': return 'Média';
      case 'RED': return 'Baixa';
      default: return 'Desconhecido';
    }
  };

  const getLimitBadge = (limit) => {
    const limits = { 'TIER_1K': '1K/dia', 'TIER_10K': '10K/dia', 'TIER_100K': '100K/dia', 'TIER_250': '250/dia', 'UNLIMITED': 'Ilimitado' };
    return limits[limit] || limit;
  };

  const getStatusBadge = (status) => {
    if (status === 'CONNECTED') return <span className="flex items-center gap-1 text-green-400 text-sm"><span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span> Conectado</span>;
    return <span className="flex items-center gap-1 text-red-400 text-sm"><span className="w-2 h-2 bg-red-400 rounded-full"></span> Desconectado</span>;
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'CRITICAL': return <XCircle className="text-red-500" size={18} />;
      case 'WARNING': return <AlertTriangle className="text-yellow-500" size={18} />;
      case 'SUCCESS': return <CheckCircle className="text-green-500" size={18} />;
      case 'RECOVERY': return <TrendingUp className="text-blue-500" size={18} />;
      default: return <Bell size={18} />;
    }
  };

  const getLast5DaysDots = (instanceId) => {
    const hist = instancesHistory[instanceId] || [];
    const dots = [];
    
    for (let i = 0; i < 5; i++) {
      const dayData = hist[i];
      if (dayData) {
        const date = new Date(dayData.date);
        const dateStr = date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
        dots.push(
          <div key={i} className="group relative">
            <div className={`w-4 h-4 rounded-full ${getQualityDot(dayData.qualityRating)} transition-transform hover:scale-125`} />
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-20">
              <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg">
                {dateStr}<br/>{getQualityDotTitle(dayData.qualityRating)}
              </div>
            </div>
          </div>
        );
      } else {
        dots.push(<div key={i} className="w-4 h-4 rounded-full bg-gray-600" title="Sem dados" />);
      }
    }
    return dots;
  };

  const getAptidaoBadge = (instanceId) => {
    const isApto = checkAptidao(instanceId);
    if (isApto === null) {
      return <span className="text-gray-500 text-sm">-</span>;
    }
    if (isApto) {
      return (
        <span className="flex items-center gap-1 text-green-400 bg-green-400/20 px-3 py-1 rounded-full text-sm font-medium">
          <ThumbsUp size={14} /> Apto
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 text-red-400 bg-red-400/20 px-3 py-1 rounded-full text-sm font-medium">
        <ThumbsDown size={14} /> Inapto
      </span>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Phone className="text-green-500" />
            Saúde do WhatsApp
          </h1>
          <p className="text-gray-400">Monitore a qualidade dos seus números</p>
        </div>
        <div className="flex items-center gap-3">
          {data?.lastChecked && (
            <span className="text-gray-500 text-sm">
              Atualizado: {new Date(data.lastChecked).toLocaleString('pt-BR')}
            </span>
          )}
          
          <button onClick={() => setShowArchived(!showArchived)} className={showArchived ? 'px-3 py-1.5 rounded-lg text-sm border border-amber-500 text-amber-400 bg-amber-500/10' : 'px-3 py-1.5 rounded-lg text-sm border border-gray-700 text-gray-400 hover:text-white'}>{showArchived ? 'Vendo arquivados' : 'Ver arquivados'}</button>
          <button onClick={() => setShowAlerts(!showAlerts)} className="relative flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-all">
            <Bell size={18} />
            Alertas
            {unreadAlerts > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{unreadAlerts}</span>
            )}
          </button>
          
          <button onClick={syncData} disabled={syncing} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${syncing ? 'bg-green-600/50 text-green-300 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-500'}`}>
            {syncing ? (<><Loader2 size={18} className="animate-spin" />Sincronizando...</>) : (<><RefreshCw size={18} />Atualizar</>)}
          </button>
        </div>
      </div>

      {/* Painel de Alertas */}
      {showAlerts && (
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white flex items-center gap-2"><Bell className="text-yellow-500" />Alertas Recentes</h3>
            {unreadAlerts > 0 && (<button onClick={markAllRead} className="text-sm text-blue-400 hover:text-blue-300">Marcar todos como lidos</button>)}
          </div>
          {alerts.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Nenhum alerta recente</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {alerts.map((alert, i) => (
                <div key={i} className={`flex items-start gap-3 p-3 rounded-lg ${alert.readAt ? 'bg-gray-700/30' : 'bg-gray-700'}`}>
                  {getAlertIcon(alert.alertType)}
                  <div className="flex-1">
                    <p className="text-white text-sm">{alert.message}</p>
                    <p className="text-gray-500 text-xs mt-1">{new Date(alert.createdAt).toLocaleString('pt-BR')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg"><Phone className="text-blue-400" size={24} /></div>
            <div><p className="text-gray-400 text-sm">Total</p><p className="text-2xl font-bold text-white">{summary.total}</p></div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg"><CheckCircle className="text-green-400" size={24} /></div>
            <div><p className="text-gray-400 text-sm">Qualidade Alta</p><p className="text-2xl font-bold text-green-400">{summary.green}</p></div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg"><AlertTriangle className="text-yellow-400" size={24} /></div>
            <div><p className="text-gray-400 text-sm">Qualidade Média</p><p className="text-2xl font-bold text-yellow-400">{summary.yellow}</p></div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg"><XCircle className="text-red-400" size={24} /></div>
            <div><p className="text-gray-400 text-sm">Qualidade Baixa</p><p className="text-2xl font-bold text-red-400">{summary.red}</p></div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg"><Signal className="text-green-400" size={24} /></div>
            <div><p className="text-gray-400 text-sm">Conectados</p><p className="text-2xl font-bold text-white">{summary.connected}</p></div>
          </div>
        </div>
      </div>

      {/* Dicas */}
      {(summary.yellow > 0 || summary.red > 0) && (
        <div className="bg-yellow-900/30 border border-yellow-700 rounded-xl p-4">
          <h3 className="text-yellow-400 font-semibold mb-2 flex items-center gap-2"><Zap size={18} />Dicas para melhorar a qualidade</h3>
          <ul className="text-yellow-200/80 text-sm space-y-1">
            <li>• Evite enviar mensagens para quem não pediu</li>
            <li>• Espere o cliente responder antes de enviar outra mensagem</li>
            <li>• Mantenha taxa de bloqueio abaixo de 1%</li>
            <li>• Use templates aprovados pelo Meta</li>
          </ul>
        </div>
      )}

      {/* Tabela de Instâncias */}
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <MessageCircle className="text-green-500" />
            Números WhatsApp ({instances.length})
              {disconnectedCount > 0 && (
                <button onClick={() => setHideDisconnected(!hideDisconnected)} className={`ml-3 text-xs px-2 py-1 rounded-lg ${hideDisconnected ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400'}`}>
                  {hideDisconnected ? `Mostrando conectados (${disconnectedCount} ocultos)` : `Ocultar ${disconnectedCount} desconectados`}
                </button>
              )}
          </h2>
          <button onClick={() => setShowAptidaoConfig(true)} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm px-3 py-1.5 rounded-lg hover:bg-gray-700 transition-colors">
            <Settings size={16} />
            Configurar Aptidão
          </button>
        </div>
        
        {instances.length === 0 ? (
          <div className="text-center py-10">
            <Phone className="mx-auto text-gray-600 mb-3" size={48} />
            <p className="text-gray-500">Nenhum número encontrado</p>
            <p className="text-gray-600 text-sm mt-1">Clique em "Atualizar" para sincronizar</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-400 text-sm border-b border-gray-700">
                  <th className="pb-3">Instância</th>
                  <th className="pb-3">Número</th>
                  <th className="pb-3">BM</th>
                  <th className="pb-3">Parceiro</th>
                  <th className="pb-3 text-center">Qualidade</th>
                  <th className="pb-3 text-center">Últimos 5 dias</th>
                  <th className="pb-3 text-center">Aptidão</th>
                  <th className="pb-3 text-center">Limite</th>
                  <th className="pb-3 text-center">Status</th>
                  <th className="pb-3 text-center"></th>
                </tr>
              </thead>
              <tbody>
                {instances.map((instance, i) => (
                  <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30 cursor-pointer" onClick={() => openModal(instance)}>
                    <td className="py-4"><span className="text-white font-medium">{instance.instanceName}</span></td>
                    <td className="py-4"><span className="text-gray-300">{instance.phoneNumber}</span></td>
                    <td className="py-4"><span className="text-gray-400 text-sm">{instance.bmName || '-'}</span></td>
                    <td className="py-4"><span className="text-purple-400 text-sm">{instance.partnerName || '-'}</span></td>
                    <td className="py-4 text-center">{getQualityBadge(instance.qualityRating)}</td>
                    <td className="py-4">
                      <div className="flex items-center justify-center gap-1">
                        {getLast5DaysDots(instance.instanceId)}
                      </div>
                    </td>
                    <td className="py-4 text-center">{getAptidaoBadge(instance.instanceId)}</td>
                    <td className="py-4 text-center">
                      <span className="text-blue-400 bg-blue-400/20 px-2 py-1 rounded text-sm">{getLimitBadge(instance.messagingLimit)}</span>
                    </td>
                    <td className="py-4 text-center">{getStatusBadge(instance.status)}</td>
                    <td className="py-4 text-center">
                      <button onClick={async (e) => { e.stopPropagation(); await api.post('/whatsapp/' + instance.id + '/' + (showArchived ? 'unarchive' : 'archive')); fetchData(); }} className="text-gray-500 hover:text-amber-400 transition-colors mr-2" title={showArchived ? 'Desarquivar' : 'Arquivar'}><Archive size={16} /></button>
                      <button className="text-gray-400 hover:text-white transition-colors"><ChevronRight size={20} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><TrendingUp className="text-blue-400" />Sobre a Classificação de Qualidade</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="bg-green-900/30 rounded-lg p-3 border border-green-800">
            <div className="flex items-center gap-2 text-green-400 font-medium mb-1"><CheckCircle size={16} /> Alta (Verde)</div>
            <p className="text-green-200/70">Ótimo desempenho. Mantenha as boas práticas!</p>
          </div>
          <div className="bg-yellow-900/30 rounded-lg p-3 border border-yellow-800">
            <div className="flex items-center gap-2 text-yellow-400 font-medium mb-1"><AlertTriangle size={16} /> Média (Amarelo)</div>
            <p className="text-yellow-200/70">Atenção! Reduza envios e melhore engajamento.</p>
          </div>
          <div className="bg-red-900/30 rounded-lg p-3 border border-red-800">
            <div className="flex items-center gap-2 text-red-400 font-medium mb-1"><XCircle size={16} /> Baixa (Vermelho)</div>
            <p className="text-red-200/70">Risco de restrição! Pare envios em massa imediatamente.</p>
          </div>
        </div>
      </div>

      {/* Modal Config Aptidão */}
      {showAptidaoConfig && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white flex items-center gap-2"><Settings className="text-blue-400" />Configurar Aptidão</h3>
                <button onClick={() => setShowAptidaoConfig(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-3">
                <label className="text-gray-400 text-sm font-medium">Lógica entre condições:</label>
                <div className="flex gap-2">
                  {['AND', 'OR'].map(l => (
                    <button key={l} onClick={() => setAptidaoConfig({...aptidaoConfig, logic: l})} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${aptidaoConfig.logic === l ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400'}`}>{l === 'AND' ? 'E (todas)' : 'OU (qualquer)'}</button>
                  ))}
                </div>
              </div>

              {(aptidaoConfig.conditions || []).map((cond, idx) => (
                <div key={idx} className="p-4 bg-gray-900/50 border border-gray-700 rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-white text-sm font-medium">Condição {idx + 1}</span>
                    {aptidaoConfig.conditions.length > 1 && (
                      <button onClick={() => setAptidaoConfig({...aptidaoConfig, conditions: aptidaoConfig.conditions.filter((_, i) => i !== idx)})} className="text-red-400 hover:text-red-300 text-sm">Remover</button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <select value={cond.condition} onChange={(e) => { const cs = [...aptidaoConfig.conditions]; cs[idx] = {...cs[idx], condition: e.target.value}; setAptidaoConfig({...aptidaoConfig, conditions: cs}); }} className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="is">TIVER</option>
                      <option value="is_not">NÃO TIVER</option>
                    </select>
                    <select value={cond.quality} onChange={(e) => { const cs = [...aptidaoConfig.conditions]; cs[idx] = {...cs[idx], quality: e.target.value}; setAptidaoConfig({...aptidaoConfig, conditions: cs}); }} className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm">
                      <option value="GREEN">Alta (Verde)</option>
                      <option value="YELLOW">Média (Amarelo)</option>
                      <option value="RED">Baixa (Vermelho)</option>
                    </select>
                    <select value={cond.days} onChange={(e) => { const cs = [...aptidaoConfig.conditions]; cs[idx] = {...cs[idx], days: parseInt(e.target.value)}; setAptidaoConfig({...aptidaoConfig, conditions: cs}); }} className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm">
                      {[1,2,3,4,5,7].map(d => <option key={d} value={d}>{d} dia{d>1?'s':''}</option>)}
                    </select>
                  </div>
                </div>
              ))}

              <button onClick={() => setAptidaoConfig({...aptidaoConfig, conditions: [...(aptidaoConfig.conditions || []), { condition: 'is', quality: 'GREEN', days: 3 }]})} className="w-full py-2.5 border border-dashed border-gray-600 rounded-lg text-gray-400 hover:text-white hover:border-gray-400 text-sm">+ Adicionar condição</button>

              <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-xl">
                <p className="text-blue-300 text-sm">
                  <strong>Critério:</strong> Apto se {aptidaoConfig.logic === 'OR' ? 'QUALQUER' : 'TODAS'} condição(ões) for(em) atendida(s).
                </p>
              </div>
            </div>
            <div className="p-4 bg-gray-900/50 border-t border-gray-700 flex gap-3">
              <button onClick={() => setShowAptidaoConfig(false)} className="flex-1 py-3 rounded-lg font-medium text-gray-400 hover:text-white hover:bg-gray-700 transition-colors">Cancelar</button>
              <button onClick={() => saveAptidaoConfig(aptidaoConfig)} className="flex-1 py-3 rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-500 transition-colors">Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes */}
      {selectedInstance && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <div>
                <h2 className="text-xl font-bold text-white">{selectedInstance.instanceName}</h2>
                <p className="text-gray-400">{selectedInstance.phoneNumber}</p>
              </div>
              <button onClick={closeModal} className="text-gray-400 hover:text-white transition-colors"><X size={24} /></button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-gray-400 text-sm mb-1">Qualidade</p>
                  <div className="flex justify-center">{getQualityBadge(selectedInstance.qualityRating)}</div>
                </div>
                <div className="bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-gray-400 text-sm mb-1">Limite</p>
                  <p className="text-blue-400 font-bold">{getLimitBadge(selectedInstance.messagingLimit)}</p>
                </div>
                <div className="bg-gray-700/50 rounded-xl p-4 text-center">
                  <p className="text-gray-400 text-sm mb-1">Aptidão</p>
                  <div className="flex justify-center">{getAptidaoBadge(selectedInstance.instanceId)}</div>
                </div>
              </div>
              
              <div className="bg-gray-700/30 rounded-xl p-4">
                <h4 className="text-white font-medium mb-3">Detalhes</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-gray-500">Nome Verificado</p><p className="text-white">{selectedInstance.verifiedName || '-'}</p></div>
                  <div><p className="text-gray-500">WABA ID</p><p className="text-white font-mono text-xs">{selectedInstance.wabaId || '-'}</p></div>
                  <div><p className="text-gray-500">Phone Number ID</p><p className="text-white font-mono text-xs">{selectedInstance.phoneNumberId || '-'}</p></div>
                  <div><p className="text-gray-500">Última Verificação</p><p className="text-white">{selectedInstance.lastChecked ? new Date(selectedInstance.lastChecked).toLocaleString('pt-BR') : '-'}</p></div>
                </div>
              </div>
              
              <div className="bg-gray-700/30 rounded-xl p-4">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2"><History size={18} className="text-blue-400" />Histórico de Qualidade (últimos 30 dias)</h4>
                {loadingHistory ? (
                  <div className="flex justify-center py-8"><Loader2 className="animate-spin text-blue-500" size={24} /></div>
                ) : history.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Nenhum histórico disponível ainda</p>
                ) : (
                  <div>
                    <div className="flex flex-wrap gap-1 mb-4">
                      {history.map((h, i) => (
                        <div key={i} className="group relative">
                          <div className={`w-6 h-6 rounded-full ${getQualityDot(h.qualityRating)} cursor-pointer hover:ring-2 hover:ring-white/50 transition-all`} />
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                            <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">{new Date(h.date).toLocaleDateString('pt-BR')}<br/>{h.qualityRating}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-4 text-xs text-gray-400">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded-full"></span> Alta</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-500 rounded-full"></span> Média</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded-full"></span> Baixa</span>
                    </div>
                  </div>
                )}
              </div>
              
              {selectedInstance.qualityRating === 'RED' && (
                <div className="bg-red-900/30 border border-red-700 rounded-xl p-4">
                  <h4 className="text-red-400 font-medium mb-2 flex items-center gap-2"><XCircle size={18} />Ações Urgentes</h4>
                  <ul className="text-red-200/80 text-sm space-y-1">
                    <li>• Pare TODOS os envios em massa imediatamente</li>
                    <li>• Responda apenas clientes que iniciarem conversa</li>
                    <li>• Aguarde 7 dias para qualidade se recuperar</li>
                  </ul>
                </div>
              )}
              
              {selectedInstance.qualityRating === 'YELLOW' && (
                <div className="bg-yellow-900/30 border border-yellow-700 rounded-xl p-4">
                  <h4 className="text-yellow-400 font-medium mb-2 flex items-center gap-2"><AlertTriangle size={18} />Recomendações</h4>
                  <ul className="text-yellow-200/80 text-sm space-y-1">
                    <li>• Reduza o volume de envios em 50%</li>
                    <li>• Priorize contatos engajados</li>
                    <li>• Monitore diariamente a qualidade</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppHealth;
